/**
 * Left sidebar for compose: client / contact emails and collected (recent) recipients.
 */
import React, { useMemo, useRef, useState } from 'react';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { AnchoredMenu } from '../../../components/ui/AnchoredMenu';
import { useAddressBookOrganizations, useAddressBookContacts } from '../../../hooks/useAddressBook';
import type { RecentRecipientItem } from '../useData';
import { sortContactPoints, type ContactPoint } from '../../address-book/contactPoints';

export type AddressBookRecipientField = 'to' | 'cc' | 'bcc';

export interface ComposeAddressBookSidebarProps {
  recentRecipients: RecentRecipientItem[];
  onPick: (recipient: string, field: AddressBookRecipientField) => void;
  onClose: () => void;
}

function isPlausibleEmail(s: string): boolean {
  const t = s.trim();
  return t.length >= 3 && t.includes('@') && !/\s/.test(t);
}

function formatRecipient(displayName: string | undefined, email: string): string {
  const name = displayName?.trim();
  if (name && !name.includes('@')) {
    const safe = name.replace(/"/g, '\\"');
    return `${safe} <${email.trim()}>`;
  }
  return email.trim();
}

/** Append recipient to a comma-separated To/Cc field if that email is not already listed. */
export function appendRecipientToField(current: string, recipient: string): string {
  const r = recipient.trim();
  if (!r) return current;
  const emailMatch = r.match(/<([^>]+)>/);
  const newEmail = (emailMatch ? emailMatch[1] : r).trim().toLowerCase();
  const tokens = current.split(/[,;]/).map((x) => x.trim()).filter(Boolean);
  for (const tok of tokens) {
    const m = tok.match(/<([^>]+)>/);
    const e = (m ? m[1] : tok).trim().toLowerCase();
    if (e === newEmail) return current;
  }
  return current ? `${current}, ${r}` : r;
}

type BookEmail = {
  key: string;
  email: string;
  label?: string;
  contactName?: string;
  contactPosition?: string;
  value: string;
};

type BookEntry = {
  key: string;
  name: string;
  subtitle?: string;
  emails: BookEmail[];
  searchText: string;
};

function emailsFromContactPoints(
  contactPoints: ContactPoint[] | undefined,
  keyPrefix: string,
  fallbackName: string | undefined,
): BookEmail[] {
  return sortContactPoints(contactPoints ?? [], 'EMAIL')
    .filter((point) => isPlausibleEmail(point.value))
    .map((point, index) => {
      const email = point.value.trim();
      const contactName = point.contactName?.trim() || fallbackName?.trim() || undefined;
      const contactPosition = point.contactPosition?.trim() || undefined;
      const displayName = contactName && !contactName.includes('@') ? contactName : undefined;
      return {
        key: `${keyPrefix}-${email}-${index}`,
        email,
        label: point.label?.trim() || undefined,
        contactName: displayName,
        contactPosition,
        value: formatRecipient(displayName, email),
      };
    });
}

function EmailRowDisplay({
  item,
  mutedClass,
  textClass,
}: {
  item: BookEmail;
  mutedClass: string;
  textClass: string;
}) {
  const name = item.contactName?.trim();
  const position = item.contactPosition?.trim();
  const email = item.email;

  if (!name && !position) {
    return <span className="truncate text-[11px] leading-tight">{email}</span>;
  }

  return (
    <span className="flex min-w-0 flex-col gap-0.5">
      {name ? (
        <span className={cn('truncate text-[11px] font-semibold leading-tight', textClass)}>{name}</span>
      ) : null}
      <span className={cn('flex min-w-0 items-center gap-1.5 text-[10px] leading-tight', mutedClass)}>
        {position ? (
          <span className="shrink-0 rounded-md bg-gray-100 px-1.5 py-0.5 font-medium text-gray-600 dark:bg-gray-800/80 dark:text-gray-300">
            {position}
          </span>
        ) : null}
        <span className="truncate opacity-90">{email}</span>
      </span>
    </span>
  );
}

function entryMatchesQuery(entry: BookEntry, q: string): boolean {
  if (!q) return true;
  return entry.searchText.includes(q);
}

export const ComposeAddressBookSidebar: React.FC<ComposeAddressBookSidebarProps> = ({
  recentRecipients,
  onPick,
  onClose,
}) => {
  const { colors } = useTheme();
  const [query, setQuery] = useState('');
  const pickerAnchorRef = useRef<HTMLElement | null>(null);
  const [pickerRecipient, setPickerRecipient] = useState<string | null>(null);

  const openFieldPicker = (recipient: string, anchor: HTMLElement) => {
    pickerAnchorRef.current = anchor;
    setPickerRecipient(recipient);
  };

  const pickField = (field: AddressBookRecipientField) => {
    if (!pickerRecipient) return;
    onPick(pickerRecipient, field);
    setPickerRecipient(null);
  };

  const fieldOptions: { field: AddressBookRecipientField; label: string }[] = [
    { field: 'to', label: 'To' },
    { field: 'cc', label: 'Cc' },
    { field: 'bcc', label: 'Bcc' },
  ];

  const { organizations: clients, isLoading: clientsLoading, isError: clientsError } =
    useAddressBookOrganizations();
  const { contacts, isLoading: contactsLoading, isError: contactsError } = useAddressBookContacts();

  const organizationEntries: BookEntry[] = useMemo(() => {
    const entries: BookEntry[] = [];
    for (const c of clients) {
      const name = c.clientName || c.clientName2 || `Organization #${c.clientId}`;
      const subtitle = c.clientName && c.clientName2?.trim() ? c.clientName2.trim() : undefined;
      const fallbackName = c.contactPerson?.trim() || undefined;
      const emails = emailsFromContactPoints(c.contactPoints, `cl-${c.clientId}`, fallbackName);
      if (emails.length === 0) continue;
      const searchText = [
        name,
        subtitle,
        c.contactPerson,
        ...emails.flatMap((emailItem) => [
          emailItem.contactName,
          emailItem.contactPosition,
          emailItem.email,
          emailItem.label,
        ]),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      entries.push({ key: `org-${c.clientId}`, name, subtitle, emails, searchText });
    }
    entries.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    return entries;
  }, [clients]);

  const peopleEntries: BookEntry[] = useMemo(() => {
    const entries: BookEntry[] = [];
    for (const ct of contacts) {
      const fn = [ct.clientContactFirstName, ct.clientContactLastName].filter(Boolean).join(' ').trim();
      const company = ct.client?.clientName?.trim();
      const name = fn || company || 'Contact';
      const subtitleParts = [
        fn && company ? company : null,
        ct.clientContactPosition?.trim() || null,
      ].filter(Boolean);
      const subtitle = subtitleParts.length > 0 ? subtitleParts.join(' · ') : undefined;
      const emails = emailsFromContactPoints(
        ct.contactPoints,
        `co-${ct.clientContactId}`,
        fn || undefined,
      );
      if (emails.length === 0) continue;
      const searchText = [
        name,
        subtitle,
        ...emails.flatMap((emailItem) => [
          emailItem.contactName,
          emailItem.contactPosition,
          emailItem.email,
          emailItem.label,
        ]),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      entries.push({ key: `person-${ct.clientContactId}`, name, subtitle, emails, searchText });
    }
    entries.sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    return entries;
  }, [contacts]);

  const collectedEntries: BookEntry[] = useMemo(() => {
    return [...recentRecipients]
      .sort((a, b) => (b.lastUsedAt || '').localeCompare(a.lastUsedAt || ''))
      .map((r) => {
        const email = r.email.trim();
        const displayName = r.name?.trim();
        const name = displayName || email;
        const emails: BookEmail[] = [{
          key: `rc-${r.id}-${email}`,
          email,
          value: displayName ? formatRecipient(displayName, email) : email,
        }];
        const searchText = [name, email].join(' ').toLowerCase();
        return {
          key: `collected-${r.id}`,
          name,
          subtitle: displayName ? 'Recently used' : undefined,
          emails,
          searchText,
        };
      });
  }, [recentRecipients]);

  const q = query.trim().toLowerCase();

  const filteredOrganizations = useMemo(
    () => organizationEntries.filter((entry) => entryMatchesQuery(entry, q)),
    [organizationEntries, q],
  );
  const filteredPeople = useMemo(
    () => peopleEntries.filter((entry) => entryMatchesQuery(entry, q)),
    [peopleEntries, q],
  );
  const filteredCollected = useMemo(
    () => collectedEntries.filter((entry) => entryMatchesQuery(entry, q)),
    [collectedEntries, q],
  );

  const loading = clientsLoading || contactsLoading;
  const loadError = clientsError || contactsError;

  const renderEntry = (entry: BookEntry) => (
    <li
      key={entry.key}
      className={cn('border-b last:border-b-0 py-1.5', colors.border)}
    >
      <div className={cn('text-[11px] font-semibold leading-tight truncate px-1', colors.text)} title={entry.name}>
        {entry.name}
      </div>
      {entry.subtitle && (
        <div className={cn('text-[10px] leading-tight truncate px-1 mt-px', colors.textMuted)} title={entry.subtitle}>
          {entry.subtitle}
        </div>
      )}
      <ul className="mt-0.5 pl-3" aria-label={`Emails for ${entry.name}`}>
        {entry.emails.map((item) => (
          <li key={item.key}>
            <button
              type="button"
              onClick={(e) => openFieldPicker(item.value, e.currentTarget)}
              className={cn(
                'group w-full text-left rounded-md pl-1 pr-1 py-1 text-[11px] leading-tight transition-colors',
                colors.text,
                'hover:bg-indigo-50 hover:text-indigo-900 dark:hover:bg-indigo-950/40 dark:hover:text-indigo-100',
                'focus:outline-none focus-visible:ring-1 focus-visible:ring-indigo-500/50',
                pickerRecipient === item.value && 'bg-indigo-50 text-indigo-900 dark:bg-indigo-950/40 dark:text-indigo-100',
              )}
              title={
                [item.contactName, item.contactPosition, item.email].filter(Boolean).join(' · ') ||
                `Add ${item.value}`
              }
              aria-haspopup="menu"
              aria-expanded={pickerRecipient === item.value}
            >
              <EmailRowDisplay
                item={item}
                textClass={colors.text}
                mutedClass={cn(
                  colors.textMuted,
                  'group-hover:text-indigo-700/90 dark:group-hover:text-indigo-200/90',
                )}
              />
            </button>
          </li>
        ))}
      </ul>
    </li>
  );

  const renderSection = (title: string, entries: BookEntry[]) => (
    <div className="mb-2">
      <h3 className={cn('text-[9px] font-semibold uppercase tracking-wider px-1 mb-0.5 sticky top-0 py-0.5', colors.textMuted, colors.surface)}>
        {title}
      </h3>
      {entries.length === 0 ? (
        <p className={cn('px-1 text-[10px] py-1', colors.textMuted)}>{q ? 'No matches.' : 'None yet.'}</p>
      ) : (
        <ul>{entries.map(renderEntry)}</ul>
      )}
    </div>
  );

  return (
    <aside
      className={cn(
        'flex flex-col h-full min-h-0 self-stretch w-[min(100vw,20rem)] flex-shrink-0 border-r',
        colors.border,
        colors.surface,
      )}
      aria-label="Address book"
    >
      <div className={cn('flex items-center justify-between gap-1 px-1.5 py-1 border-b flex-shrink-0', colors.border)}>
        <span className={cn('text-[11px] font-semibold', colors.text)}>Address book</span>
        <button
          type="button"
          onClick={onClose}
          className={cn('p-1 rounded', colors.textMuted, colors.interactiveHover)}
          title="Close"
          aria-label="Close address book"
        >
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      <div className="px-1.5 py-1 flex-shrink-0">
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search…"
          className={cn(
            'w-full text-[11px] rounded border px-1.5 py-1 outline-none focus:ring-1 focus:ring-indigo-500/40',
            colors.border,
            colors.surface,
            colors.text,
          )}
          aria-label="Search address book"
        />
      </div>
      <div className="flex-1 min-h-0 overflow-y-auto px-1 pb-2">
        {loading && (
          <div className={cn('flex items-center gap-1.5 px-1 py-2 text-[10px]', colors.textMuted)}>
            <svg className="animate-spin h-3 w-3 flex-shrink-0" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
            Loading contacts…
          </div>
        )}
        {!loading && loadError && (
          <p className={cn('px-1 py-2 text-[10px]', colors.textMuted)}>
            Could not load address book contacts. Check your connection and try again.
          </p>
        )}
        {!loading && !loadError && (
          <>
            {renderSection('Organizations', filteredOrganizations)}
            {renderSection('People', filteredPeople)}
            {renderSection('Collected', filteredCollected)}
          </>
        )}
      </div>
      <AnchoredMenu
        open={pickerRecipient != null}
        onClose={() => setPickerRecipient(null)}
        anchorRef={pickerAnchorRef}
        align="start"
        className={cn('min-w-[6.5rem]', colors.surface, colors.border, colors.text)}
      >
        <p className={cn('px-2.5 py-1 text-[9px] font-semibold uppercase tracking-wide border-b', colors.textMuted, colors.border)}>
          Add to
        </p>
        {fieldOptions.map(({ field, label }) => (
          <button
            key={field}
            type="button"
            role="menuitem"
            onClick={() => pickField(field)}
            className={cn(
              'w-full text-left px-2.5 py-1.5 text-[11px] font-medium transition-colors',
              colors.text,
              'hover:bg-indigo-50 hover:text-indigo-900 dark:hover:bg-indigo-950/40 dark:hover:text-indigo-100',
            )}
          >
            {label}
          </button>
        ))}
      </AnchoredMenu>
    </aside>
  );
};

export default ComposeAddressBookSidebar;
